import React, { useEffect, useRef, useState } from "react";
import ReactHtmlParser from 'react-html-parser';
import { toast } from "react-toastify";
import { isValidPhoneNumber } from "libphonenumber-js/mobile";
import CustomPhoneInput from "./CustomPhoneInput";
import ButtonComp from "./ButtonComp";
import API from "api";
import "../../assets/css/PteConsultation.css";
import LiveClass from "../../assets/images/public-pages/black-call.svg";
import AIPowered from "../../assets/images/public-pages/black-email.svg";
import Feedback from "../../assets/images/public-pages/black-location.svg";
import CustomerSupport from "../../assets/images/public-pages/customer-support.svg";
import Clock from "../../assets/images/public-pages/black-clock.svg";

const EMAIL_REGEX = /^[a-zA-Z0-9]([a-zA-Z0-9._%+\-]*[a-zA-Z0-9])?@[a-zA-Z0-9]([a-zA-Z0-9\-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9\-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
// Allow Unicode letters (\p{L}) + combining marks (\p{M}) so diacritics like
// é/ñ survive whether they're precomposed or decomposed, plus space, apostrophe, hyphen.
const NAME_DISALLOWED_REGEX = /[^\p{L}\p{M} '\-]/gu;
const NAME_ALLOWED_REGEX = /^[\p{L}\p{M} '\-]+$/u;

const normalizeName = (value) => (value || "").toString().normalize("NFC");

const validateField = (field, value) => {
    switch (field) {
        case "fullName": {
            const v = normalizeName(value).trim();
            if (!v) return "Name is required.";
            if (!NAME_ALLOWED_REGEX.test(v)) {
                return "Please use letters, spaces, apostrophes, or hyphens only.";
            }
            return "";
        }
        case "email": {
            const v = (value || "").toString().trim();
            if (!v) return "Email is required.";
            if (!EMAIL_REGEX.test(v) || /\.\./.test(v.split("@")[0])) return "Please enter a valid email address.";
            return "";
        }
        case "phone": {
            const v = (value || "").toString().trim();
            if (!v) return "Phone Number is required.";
            if (!isValidPhoneNumber(v)) {
                return "Please enter a valid phone number for the selected country.";
            }
            return "";
        }
        case "message":
            if (!(value || "").toString().trim()) return "Message is required.";
            return "";
        default:
            return "";
    }
};

const BULLETS = [
    { strong: "50,000+", rest: "students trained" },
    { strong: "85%", rest: "achieve target score on first attempt" },
    { strong: "8+ years", rest: "of PTE coaching experience" },
    { strong: "4.8★", rest: "Google Rating" },
];

const ContactUs = () => {
    const [formData, setFormData] = useState({
        fullName: "",
        email: "",
        phone: "",
        message: "",
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const submittingRef = useRef(false);

    const setField = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            // clear the error as soon as the user starts fixing it
            setErrors((prev) => {
                const next = { ...prev };
                delete next[field];
                return next;
            });
        }
    };

    const handleChange = (field) => (e) => setField(field, e.target.value);
    const handleNameChange = (e) =>
        setField("fullName", normalizeName(e.target.value).replace(NAME_DISALLOWED_REGEX, ""));
    const handleBlur = (field) => () => {
        const message = validateField(field, formData[field]);
        setErrors((prev) => {
            const next = { ...prev };
            if (message) next[field] = message;
            else delete next[field];
            return next;
        });
    };
    const handlePhoneChange = (value) => setField("phone", value);

    const validateAll = () => {
        const next = {};
        Object.keys(formData).forEach((field) => {
            const message = validateField(field, formData[field]);
            if (message) next[field] = message;
        });
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async (e) => {
        if (e && typeof e.preventDefault === "function") e.preventDefault();
        if (submittingRef.current) return; // synchronous block — runs before re-render

        if (!validateAll()) return;

        submittingRef.current = true;
        setSubmitting(true);

        try {
            const res = await API({
                method: "POST",
                url: "post/contact",
                data: { name: formData.fullName, email: formData.email, phone: formData.phone, message: formData.message },
            });
            if (res?.status === 200) {
                toast.success(res?.data?.response?.message || "Thanks! Our team will contact you shortly.");
                setFormData({ fullName: "", email: "", phone: "", message: "" });
                setErrors({});
            } else {
                toast.error("Something went wrong. Please try again.");
            }
        } catch (err) {
            if (err?.response?.status === 422 && err.response.data?.errors) {
                const serverErrors = err.response.data.errors;
                const mapped = {};
                if (serverErrors.name) mapped.fullName = serverErrors.name[0];
                if (serverErrors.email) mapped.email = serverErrors.email[0];
                if (serverErrors.phone) mapped.phone = serverErrors.phone[0];
                if (serverErrors.message) mapped.message = serverErrors.message[0];
                setErrors((prev) => ({ ...prev, ...mapped }));
            } else {
                toast.error("Something went wrong. Please try again.");
            }
        } finally {
            submittingRef.current = false;
            setSubmitting(false);
        }
    };

    const features = [
        {
            title: "PHONE",
            description: "+61 455 566 654",
            img: LiveClass
        },
        {
            title: "EMAIL",
            description: "info@languageacademy.com.au",
            img: AIPowered
        },
        {
            title: "HEAD OFFICE",
            description: "46 George Street Parramatta NSW 2150",
            img: Feedback
        },
        {
            title: "ONLINE SUPPORT",
            description: "+61 455 566 654",
            img: CustomerSupport
        },
        {
            title: "OPENING HOURS",
            description: 'Mon - Fri : 10:00 AM to 07:00 PM AEDT<br/> Sat - Sun : 10:00 AM to 05:00 PM AEDT',
            img: Clock
        },
    ];

//     return (
//    <div className="custom-container">
//             <section className="pte-consultation custom-container contact-us-section">
//             <div className="pte-consultation__container p-80 w-100 h-100">
//                 <form className="pte-consultation__form contact-form-col" onSubmit={handleSubmit} noValidate>
//                    <div className="audio-transcript-text p-0 border-0 mt-0">
//                 <p className="FS28 fw-600" style={{color:"#19181C"}}>
//                     Send Us a Message
//                 </p>
//                 <span className="FS15FW400">Fill out the form below and we'll get back to you within 2 hours.</span>
//               <hr/>
//               </div>
//                     <div className="pte-consultation__row gap-3">
//                         <label className="pte-consultation__field">
//                             <span className="pte-consultation__label">Full Name</span>
//                             <input
//                                 type="text"
//                                 className={`pte-consultation__input`}
//                                 placeholder="Enter name"
//                                 value={formData.fullName}
//                                 onChange={handleNameChange}
//                                 onBlur={handleBlur("fullName")}
//                                 aria-invalid={!!errors.fullName}
//                                 maxLength={30}
//                             />
//                             {errors.fullName && (
//                                 <span className="pte-consultation__error">{errors.fullName}</span>
//                             )}
//                         </label>
//                         <label className="pte-consultation__field">
//                             <span className="pte-consultation__label">Email Address</span>
//                             <input
//                                 type="email"
//                                 className={`pte-consultation__input`}
//                                 placeholder="Enter email address"
//                                 value={formData.email}
//                                 onChange={handleChange("email")}
//                                 onBlur={handleBlur("email")}
//                                 aria-invalid={!!errors.email}
//                             />
//                             {errors.email && (
//                                 <span className="pte-consultation__error">{errors.email}</span>
//                             )}
//                         </label>
//                     </div>

//                     <div className="pte-consultation__row">
//                         <div
//                             className={`pte-consultation__field${errors.phone ? " pte-consultation__field--invalid" : ""
//                                 }`}
//                         >
//                             <span className="pte-consultation__label">Phone Number</span>
//                             <CustomPhoneInput
//                                 value={formData.phone}
//                                 onChange={handlePhoneChange}
//                                 onBlur={handleBlur("phone")}
//                             />
//                             {errors.phone && (
//                                 <span className="pte-consultation__error">{errors.phone}</span>
//                             )}
//                         </div>
//                     </div>
//                            <label className="pte-consultation__field" style={{flex:"none"}}>
//                             <span className="pte-consultation__label">Your Message</span>
//                         <textarea
//                             className={`pte-consultation__input`}
//                             placeholder="Tells us about your goals, timeline, or any questions...."
//                             value={formData.message}
//                             onChange={handleChange("message")}
//                             onBlur={handleBlur("message")}
//                             aria-invalid={!!errors.message}
//                             rows={4}
//                             style={{ height: "auto", minHeight: "120px", resize: "vertical" }}
//                         />
//                             {errors.message && (
//                                 <span className="pte-consultation__error">{errors.message}</span>
//                             )}
//                         </label>
//                     <div
//                         className={`pte-consultation__submit-wrap${submitting ? " pte-consultation__submit-wrap--disabled" : ""
//                             }`}
//                     >
//                         <ButtonComp
//                             btnText={submitting ? "Sending..." : "Send My Enquiry"}
//                             link={submitting ? () => { } : handleSubmit}
//                         />                 
//                     </div>
//                      <p className="FS14FW600 fw-400 mb-0" style={{color:"#929498"}}>We respect your privacy. Your details will never be shared.</p>
//                 </form>
//                 <div className="custom-container contact-info-col p-0 m-0">
//                     <div>
//                         <div className="master-cards contact-us-features">
//                             <div className="master-card card3 border-0 mx-2 mx-lg-0">
//                                 <div className="master-card card3 border-0 d-flex flex-column gap-2">
//                                    <div>
//                                      <h1 className="FS28 fw-600 top-section-text mb-2">
//                                         Make Your Learning Meaningful
//                                     </h1>
//                                     <p className="FS15FW400" style={{ color: "#B4B4B4" }}>We have the expertise to help with university, residency, or career.</p>
//                                    </div>
//                                     {features.map((item, index) => (
//                                         <>
//                                             <hr />
//                                             <div key={index} className="mapOuter d-flex gap-3">
//                                                 <img src={item?.img} alt="Check" className="list-data w-44" style={{ marginTop: "6px" }} />
//                                                 <div style={{wordBreak:"break-word"}}>
//                                                     <h3 className="text-gray-pricing FS16FW500 fw-600 mb-1">{item?.title}</h3>
//                                                     <p className="top-section-text FS20FW500 mb-0">{item?.description}</p>
//                                                 </div>
//                                             </div>
//                                         </>
//                                     ))}
//                                 </div>
//                             </div>
//                         </div>
//                     </div>
//                 </div>
//             </div>           
//         </section>
//    </div>
//     );

return (
  <div className="custom-container">
    <section className="pte-consultation custom-container contact-us-section px-0">
      <div className="pte-consultation__container p-80 w-100">

        <form className="pte-consultation__form contact-form-col" onSubmit={handleSubmit} noValidate>
          <div className="audio-transcript-text p-0 border-0 mt-0">
            <p className="FS28 fw-600" style={{ color: "#19181C" }}>Send Us a Message</p>
            <span className="FS15FW400">Fill out the form below and we'll get back to you within 24 hours.</span>
            <hr />
          </div>

          <div className="pte-consultation__row gap-3">
            <label className="pte-consultation__field">
              <span className="pte-consultation__label">Full Name</span>
              <input type="text" className="pte-consultation__input" placeholder="Enter name"
                value={formData.fullName} onChange={handleNameChange} onBlur={handleBlur("fullName")}
                aria-invalid={!!errors.fullName} maxLength={30} />
              {errors.fullName && <span className="pte-consultation__error">{errors.fullName}</span>}
            </label>
            <label className="pte-consultation__field">
              <span className="pte-consultation__label">Email Address</span>
              <input type="email" className="pte-consultation__input" placeholder="Enter email address"
                value={formData.email} onChange={handleChange("email")} onBlur={handleBlur("email")}
                aria-invalid={!!errors.email} />
              {errors.email && <span className="pte-consultation__error">{errors.email}</span>}
            </label>
          </div>

          <div className="pte-consultation__row">
            <div className={`pte-consultation__field${errors.phone ? " pte-consultation__field--invalid" : ""}`}>
              <span className="pte-consultation__label">Phone Number</span>
              <CustomPhoneInput value={formData.phone} onChange={handlePhoneChange} onBlur={handleBlur("phone")} />
              {errors.phone && <span className="pte-consultation__error">{errors.phone}</span>}
            </div>
          </div>

          <label className="pte-consultation__field" style={{ flex: "none" }}>
            <span className="pte-consultation__label">Your Message</span>
            <textarea className="pte-consultation__input" placeholder="Tells us about your goals..."
              value={formData.message} onChange={handleChange("message")} onBlur={handleBlur("message")}
              aria-invalid={!!errors.message} rows={4}
              style={{ height: "auto", minHeight: "120px", resize: "vertical" }} />
            {errors.message && <span className="pte-consultation__error">{errors.message}</span>}
          </label>

          <div className={`pte-consultation__submit-wrap${submitting ? " pte-consultation__submit-wrap--disabled" : ""}`}>
            <ButtonComp btnText={submitting ? "Sending..." : "Send My Enquiry"}
              link={submitting ? () => {} : handleSubmit} />
          </div>
          <p className="FS14FW600 fw-400 mb-0" style={{ color: "#929498" }}>
            We respect your privacy. Your details will never be shared.
          </p>
        </form>

        {/* ✅ Flattened — no .row, no duplicate wrappers */}
        <div className="custom-container contact-info-col p-0">
          <div className="master-card card3 border-0 d-flex flex-column gap-2 mx-0 contact-us-features">
            <div>
              <h1 className="FS28 fw-600 top-section-text mb-2">Make Your Learning Meaningful</h1>
              <p className="FS15FW400" style={{ color: "#B4B4B4" }}>
                We have the expertise to help with university, residency, or career.
              </p>
            </div>
            {features.map((item, index) => (
              <React.Fragment key={index}>
                <hr />
                <div className="mapOuter d-flex gap-3">
                  <img src={item?.img} alt="Check" className="list-data w-44" style={{ marginTop: "6px" }} />
                  <div style={{ wordBreak: "break-word" }}>
                    <h3 className="text-gray-pricing FS16FW500 fw-600 mb-1">{item?.title}</h3>
                    <p className="top-section-text FS20FW500 mb-0">{ReactHtmlParser(item?.description)}</p>
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

      </div>
    </section>
  </div>
);

};

export default ContactUs;
